import { Calendar, Clock } from '@creditornot/cb-icons';
import { Tag } from '@creditornot/cb-tag';
import { DateTime } from 'luxon';
import R from 'ramda';
import React from 'react';
import { Field, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { RFFFieldErrorWrapper } from 'common/components';
import { WeeklyScheduleInput } from 'common/components/weekly-schedules';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { useRowOverrideContext } from './WithOverrideToggle';
import { BaseField } from './types';
import { useOfferOverridesContext } from '../../../context/OfferOverridesContext';
import ScheduleDatePicker from '../../ScheduleDatePicker';
import { TimeZoneNote } from '../../TimeZoneNote';
import { FormRowRenderer } from '../FormRowRenderer';
import { NewWeeklyScheduleView } from '../NewWeeklyScheduleView';
import {
  ColumnContainer,
  DisplayText,
  PreviewRowContainer,
  RadioWrapper,
  StyledInputContainer,
  StyledRadio,
  WeeklyScheduleWrapper,
} from '../OfferDetailsFormFields.styles';
import { FormValues } from '../types';

const StyledTag = styled(Tag).attrs({ variant: 'lightBlack' })`
  display: inline-flex;
  align-items: baseline;
`;

const StyledCalendar = styled(Calendar).attrs({ width: 12, height: 12 })`
  margin-inline-end: 4px;
`;

const StyledClock = styled(Clock).attrs({ width: 12, height: 12 })`
  margin-inline-end: 4px;
`;

const StyledColumnContainer = styled(ColumnContainer)`
  margin-block-start: 24px;
`;

const ScheduleTable = styled.section`
  display: table;

  tr > td {
    padding-block-end: 12px;

    &:last-child {
      padding-block-end: unset;
    }
  }

  tr > td:first-child {
    padding-inline-end: 8px;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Availability = ({ sidePanelMode, offer }: BaseField) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const intl = useIntl();

  const { values } = useFormState<FormValues>();
  const { maybeOverriddenOffer, getOfferAttrHasOverride } = useOfferOverridesContext();

  const { isOverrideToggleOn, isRowGroupEnabledForOverride } = useRowOverrideContext();

  const isAvailabilityOverridden = getOfferAttrHasOverride('weekly_availability');

  const availabilityPeriodFieldNameToUse = isOverrideToggleOn ? 'availabilityPeriodOverride' : 'availabilityPeriod';
  const useWeeklyAvailabilityFieldNameToUSe = isOverrideToggleOn
    ? 'useWeeklyAvailabilityOverride'
    : 'useWeeklyAvailability';
  const useWeeklyAvailabilityValueToUse = values[useWeeklyAvailabilityFieldNameToUSe];
  const weeklyAvailabilityFieldNameToUse = isOverrideToggleOn ? 'weeklyAvailabilityOverride' : 'weeklyAvailability';

  const start = maybeOverriddenOffer?.enabled.after ? DateTime.fromISO(maybeOverriddenOffer.enabled.after) : undefined;
  const end = maybeOverriddenOffer?.enabled.before ? DateTime.fromISO(maybeOverriddenOffer.enabled.before) : undefined;

  const previewComponent = (
    <PreviewRowContainer>
      <Left>
        <ScheduleTable>
          {start && (
            <tr>
              <td>
                <FormattedMessage id="special.availability-starting-label" />
              </td>
              <td>
                <StyledTag>
                  <StyledCalendar />
                  <span>{start.toFormat('d.M.y (HH:mm)')}</span>
                </StyledTag>
              </td>
            </tr>
          )}

          {end && (
            <tr>
              <td>
                <FormattedMessage id="special.availability-ending-label" />
              </td>
              <td>
                <StyledTag>
                  <StyledCalendar />
                  <span>{end.toFormat('d.M.y (HH:mm)')}</span>
                </StyledTag>
              </td>
            </tr>
          )}

          {!end && start && (
            <tr>
              <td>
                <FormattedMessage id="special.availability-ending-label" />
              </td>
              <td>
                <StyledTag>
                  <StyledCalendar />
                  <FormattedMessage id="special.availability-ongoing-label" />
                </StyledTag>
              </td>
            </tr>
          )}

          {!start && !end && (
            <DisplayText>
              <FormattedMessage id="time-of-week.always" />
            </DisplayText>
          )}

          {!maybeOverriddenOffer && (
            <tr>
              <td>
                <FormattedMessage id="special.availability-during-label" />
              </td>
              <td>
                <StyledTag>
                  <StyledClock />
                  <FormattedMessage id="special.availability-opening-hours-label" />
                </StyledTag>
              </td>
            </tr>
          )}
        </ScheduleTable>
        {maybeOverriddenOffer?.weekly_availability && maybeOverriddenOffer?.weekly_availability?.length > 0 && (
          <NewWeeklyScheduleView weeklySchedule={maybeOverriddenOffer.weekly_availability} />
        )}
      </Left>
    </PreviewRowContainer>
  );

  const editComponent = (
    <>
      <Field name={availabilityPeriodFieldNameToUse}>
        {field => (
          <RFFFieldErrorWrapper field={field} showErrorImmediately>
            {input => (
              <>
                <StyledInputContainer
                  label={intl.formatMessage({ id: 'special.availability-picker-starting-label' })}
                  style={{ marginBlockEnd: '12px' }}
                >
                  <ScheduleDatePicker
                    displayUtcHint
                    onChange={value => {
                      input.onChange({ ...input.value, start: value });
                    }}
                    value={input.value.start}
                  />
                </StyledInputContainer>
                <StyledInputContainer label={intl.formatMessage({ id: 'special.availability-picker-ending-label' })}>
                  <ScheduleDatePicker
                    displayUtcHint
                    onChange={value => {
                      input.onChange({ ...input.value, end: value });
                    }}
                    value={input.value.end}
                  />
                </StyledInputContainer>
              </>
            )}
          </RFFFieldErrorWrapper>
        )}
      </Field>
      <TimeZoneNote />

      <StyledColumnContainer>
        <Field name={useWeeklyAvailabilityFieldNameToUSe}>
          {({ input }) => (
            <RadioWrapper>
              <StyledRadio checked={!input.value} onChange={() => input.onChange(!input.value)}>
                <FormattedMessage id="menu-item.time-available-always" />
              </StyledRadio>
              <StyledRadio checked={input.value} onChange={() => input.onChange(!input.value)}>
                <FormattedMessage id="menu-item.time-available-sometimes" />
              </StyledRadio>
            </RadioWrapper>
          )}
        </Field>

        {useWeeklyAvailabilityValueToUse && (
          <Field name={weeklyAvailabilityFieldNameToUse} isEqual={R.equals}>
            {({ input, meta }) => (
              <WeeklyScheduleWrapper>
                <WeeklyScheduleInput
                  onChange={input.onChange}
                  pristine={!!meta.pristine}
                  select24HoursLabel={intl.formatMessage({ id: 'weeklyScheduleInput.select24Hours' })}
                  value={input.value}
                />
              </WeeklyScheduleWrapper>
            )}
          </Field>
        )}
      </StyledColumnContainer>
    </>
  );

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      label={intl.formatMessage({ id: 'special.shared-menu-availability' })}
      previewComponent={previewComponent}
      editComponent={editComponent}
      shouldDisplayCreateTag={false}
      isOverridden={isAvailabilityOverridden}
      canBeOverridden={isRowGroupEnabledForOverride}
      sidePanelMode={sidePanelMode}
      offer={offer}
    />
  );
};
